import React, { useState, useEffect, useMemo, useRef } from 'react';
import { API_URL } from '../../config/api';
import { 
  Plus, Search, Mail, Copy, Check, Clock, AlertCircle, Calendar,
  ChevronDown, ChevronUp, Trash2, Edit2, X, History, Building2,
  MapPin, User, Briefcase, MessageSquare, CheckCircle2, ArrowRight, XCircle
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';

const STATUS_OPTIONS = [
  { value: 'scheduled', label: 'Scheduled for Initial Outreach', color: 'bg-slate-100 text-slate-700', icon: Clock },
  { value: 'initial_outreach', label: 'Initial Outreach Sent', color: 'bg-blue-100 text-blue-700', icon: Mail },
  { value: 'first_followup', label: 'First Follow Up Sent', color: 'bg-purple-100 text-purple-700', icon: ArrowRight },
  { value: 'second_followup', label: '2nd Follow Up Sent', color: 'bg-indigo-100 text-indigo-700', icon: ArrowRight },
  { value: 'final_followup', label: 'Final Follow Up Sent', color: 'bg-orange-100 text-orange-700', icon: AlertCircle },
  { value: 'email_not_working', label: 'Email Not Functioning', color: 'bg-red-100 text-red-700 border border-red-300', icon: XCircle },
  { value: 'other', label: 'Other', color: 'bg-gray-100 text-gray-700', icon: MessageSquare }
];

// Separate component to prevent re-renders from causing input focus loss
const MarkCompleteInline = ({ contactId, markCompleteForm, setMarkCompleteForm, handleMarkComplete, setShowMarkComplete }) => {
  const [localForm, setLocalForm] = useState(markCompleteForm);
  
  // Pass local form data directly to handleMarkComplete
  const handleConfirm = () => {
    handleMarkComplete(contactId, localForm);
  };

  return (
    <div className="mt-3 pt-3 border-t border-gray-200 bg-gray-50 -mx-4 -mb-4 p-4 rounded-b-xl">
      <p className="text-sm font-medium text-gray-700 mb-2">Mark as completed & set next action:</p>
      <select
        value={localForm.next_status}
        onChange={(e) => setLocalForm({ ...localForm, next_status: e.target.value })}
        className="w-full h-10 px-3 rounded-lg border border-gray-200 mb-2 text-sm"
        data-testid="mark-complete-status-select"
      >
        {STATUS_OPTIONS.map(opt => (
          <option key={opt.value} value={opt.value}>{opt.label}</option>
        ))}
      </select>
      {localForm.next_status !== 'email_not_working' && (
        <input
          type="date"
          value={localForm.next_action_date}
          onChange={(e) => setLocalForm({ ...localForm, next_action_date: e.target.value })}
          className="w-full h-10 px-3 rounded-lg border border-gray-200 mb-2 text-sm"
          placeholder="Next action date"
          data-testid="mark-complete-date-input"
        />
      )}
      {localForm.next_status !== 'email_not_working' && (
        <input
          type="text"
          value={localForm.next_action_description}
          onChange={(e) => setLocalForm({ ...localForm, next_action_description: e.target.value })}
          className="w-full h-10 px-3 rounded-lg border border-gray-200 mb-2 text-sm"
          placeholder="Describe the next action..."
          data-testid="mark-complete-desc-input"
        />
      )}
      <div className="flex gap-2">
        <Button size="sm" onClick={handleConfirm} className="bg-green-600 hover:bg-green-700">
          Confirm
        </Button>
        <Button size="sm" variant="outline" onClick={() => setShowMarkComplete(null)}>
          Cancel
        </Button>
      </div>
    </div>
  );
};

const OutreachDatabase = () => {
  const [contacts, setContacts] = useState([]);
  const [grouped, setGrouped] = useState({ overdue: [], today: [], upcoming: [], no_date: [] });
  const [stats, setStats] = useState({ overdue: 0, today: 0, upcoming: 0, no_date: 0 });
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingContact, setEditingContact] = useState(null);
  const [selectedContact, setSelectedContact] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [copiedText, setCopiedText] = useState(null);
  const [showCompanyDropdown, setShowCompanyDropdown] = useState(false);
  const [saving, setSaving] = useState(false);
  const [showMarkComplete, setShowMarkComplete] = useState(null);
  const [expandedGroups, setExpandedGroups] = useState({ overdue: true, today: true, upcoming: true, no_date: true });
  const [highlightedContact, setHighlightedContact] = useState(null);
  const [hoveredCompany, setHoveredCompany] = useState(null);
  const contactRefs = useRef({});
  

  const emptyForm = {
    email: '',
    name: '',
    position: '',
    location: '',
    company_name: '',
    status: 'scheduled',
    status_other_description: '',
    notes: '',
    last_action_date: new Date().toISOString().split('T')[0],
    next_action_date: '',
    next_action_description: ''
  };

  const [formData, setFormData] = useState(emptyForm);
  const [historyForm, setHistoryForm] = useState({ date: '', action: '', notes: '' });
  const [markCompleteForm, setMarkCompleteForm] = useState({ 
    next_status: 'initial_outreach', 
    next_action_date: '', 
    next_action_description: '' 
  });

  useEffect(() => {
    fetchContacts();
  }, []);

  // Build company contacts map
  const companyContactsMap = useMemo(() => {
    const map = {};
    contacts.forEach(contact => {
      const company = contact.company_name.toLowerCase();
      if (!map[company]) {
        map[company] = [];
      }
      map[company].push(contact);
    });
    return map;
  }, [contacts]);

  const getCompanyContacts = (companyName) => {
    return companyContactsMap[companyName.toLowerCase()] || [];
  };

  // Get unique company names for autocomplete
  const uniqueCompanyNames = useMemo(() => {
    const names = new Set();
    contacts.forEach(contact => {
      if (contact.company_name) {
        names.add(contact.company_name);
      }
    });
    return Array.from(names).sort((a, b) => a.toLowerCase().localeCompare(b.toLowerCase()));
  }, [contacts]);

  // Filter company suggestions based on input
  const companySuggestions = useMemo(() => {
    if (!formData.company_name || formData.company_name.length < 1) return [];
    const query = formData.company_name.toLowerCase();
    return uniqueCompanyNames.filter(name => 
      name.toLowerCase().includes(query) && name.toLowerCase() !== query
    ).slice(0, 5); // Limit to 5 suggestions
  }, [formData.company_name, uniqueCompanyNames]);

  const fetchContacts = async () => {
    try {
      const res = await fetch(`${API_URL}/api/admin/outreach/contacts`);
      const data = await res.json();
      setContacts(data.contacts || []);
      setGrouped(data.grouped || { overdue: [], today: [], upcoming: [], no_date: [] });
      setStats(data.stats || { overdue: 0, today: 0, upcoming: 0, no_date: 0 });
    } catch (err) {
      console.error('Failed to fetch contacts:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const submitData = { ...formData };
      // Clear next action date for email_not_working status
      if (submitData.status === 'email_not_working') {
        submitData.next_action_date = '';
        submitData.next_action_description = '';
      }
      
      const url = editingContact 
        ? `${API_URL}/api/admin/outreach/contacts/${editingContact}`
        : `${API_URL}/api/admin/outreach/contacts`;
      
      const res = await fetch(url, {
        method: editingContact ? 'PUT' : 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(submitData)
      });
      
      if (res.ok) {
        setShowForm(false);
        setEditingContact(null);
        setFormData(emptyForm);
        fetchContacts();
      }
    } catch (err) {
      console.error('Failed to save contact:', err);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (contactId) => {
    if (!window.confirm('Delete this contact? This cannot be undone.')) return;
    try {
      await fetch(`${API_URL}/api/admin/outreach/contacts/${contactId}`, { method: 'DELETE' });
      fetchContacts();
      if (selectedContact?.id === contactId) setSelectedContact(null);
    } catch (err) {
      console.error('Failed to delete contact:', err);
    }
  };

  const handleMarkComplete = async (contactId, formData = null) => {
    // Use passed formData or fall back to state (for backward compatibility)
    const data = formData || markCompleteForm;
    
    try {
      const params = new URLSearchParams({
        next_status: data.next_status,
        ...(data.next_action_date && { next_action_date: data.next_action_date }),
        ...(data.next_action_description && { next_action_description: data.next_action_description })
      });
      
      const res = await fetch(
        `${API_URL}/api/admin/outreach/contacts/${contactId}/mark-action-complete?${params}`,
        { method: 'POST' }
      );
      
      if (res.ok) {
        setShowMarkComplete(null);
        setMarkCompleteForm({ next_status: 'initial_outreach', next_action_date: '', next_action_description: '' });
        fetchContacts();
      }
    } catch (err) {
      console.error('Failed to mark complete:', err);
    }
  };

  const handleAddHistory = async (contactId) => {
    try {
      const res = await fetch(`${API_URL}/api/admin/outreach/contacts/${contactId}/history`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(historyForm)
      });
      
      if (res.ok) {
        const data = await res.json();
        setSelectedContact(data.contact);
        setHistoryForm({ date: '', action: '', notes: '' });
        fetchContacts();
      }
    } catch (err) {
      console.error('Failed to add history:', err);
    }
  };

  const copyText = (text, type = 'text') => {
    navigator.clipboard.writeText(text);
    setCopiedText(text);
    setTimeout(() => setCopiedText(null), 2000);
  };

  const openEdit = (contact) => {
    setFormData({
      email: contact.email,
      name: contact.name,
      position: contact.position || '',
      location: contact.location || '',
      company_name: contact.company_name,
      status: contact.status,
      status_other_description: contact.status_other_description || '',
      notes: contact.notes || '',
      last_action_date: contact.last_action_date || '',
      next_action_date: contact.next_action_date || '',
      next_action_description: contact.next_action_description || ''
    });
    setEditingContact(contact.id);
    setShowForm(true);
  };

  const scrollToContact = (contactId) => {
    setHoveredCompany(null);
    setHighlightedContact(contactId);
    
    // Find and scroll to the contact
    const element = contactRefs.current[contactId];
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
    
    // Remove highlight after 3 seconds
    setTimeout(() => setHighlightedContact(null), 3000);
  };

  const getStatusInfo = (status) => STATUS_OPTIONS.find(s => s.value === status) || STATUS_OPTIONS[6];

  const formatDate = (dateStr) => {
    if (!dateStr) return 'Not set';
    try {
      return new Date(dateStr).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
    } catch {
      return dateStr;
    }
  };

  const isOverdue = (dateStr) => {
    if (!dateStr) return false;
    return dateStr < new Date().toISOString().split('T')[0];
  };

  const isToday = (dateStr) => {
    if (!dateStr) return false;
    return dateStr === new Date().toISOString().split('T')[0];
  };

  const filteredGrouped = useMemo(() => {
    if (!searchQuery) return grouped;
    
    const query = searchQuery.toLowerCase();
    const filterFn = (contact) => 
      contact.name.toLowerCase().includes(query) ||
      contact.email.toLowerCase().includes(query) ||
      contact.company_name.toLowerCase().includes(query) ||
      (contact.location || '').toLowerCase().includes(query);
    
    return {
      overdue: grouped.overdue.filter(filterFn),
      today: grouped.today.filter(filterFn),
      upcoming: grouped.upcoming.filter(filterFn),
      no_date: grouped.no_date.filter(filterFn)
    };
  }, [grouped, searchQuery]);

  const toggleGroup = (group) => {
    setExpandedGroups(prev => ({ ...prev, [group]: !prev[group] }));
  };

  // Company Hover Tooltip - Mobile friendly with click support
  const CompanyTooltip = ({ companyName, currentContactId, onClose }) => {
    const companyContacts = getCompanyContacts(companyName);
    if (companyContacts.length <= 1) return null;

    return (
      <div className="absolute z-50 left-0 sm:left-auto right-0 top-full mt-2 bg-white rounded-xl shadow-2xl border border-gray-200 overflow-hidden w-[calc(100vw-2rem)] sm:w-80 max-w-sm">
        <div className="bg-gradient-to-r from-blue-500 to-blue-600 px-4 py-3 flex items-center justify-between">
          <p className="text-sm font-semibold text-white">
            {companyContacts.length} contacts at {companyName}
          </p>
          <button onClick={onClose} className="text-white/80 hover:text-white p-1">
            <X className="w-4 h-4" />
          </button>
        </div>
        <div className="p-2 max-h-[250px] overflow-y-auto">
          {companyContacts.map(c => (
            <button
              key={c.id}
              onClick={() => { scrollToContact(c.id); onClose(); }}
              className={`w-full text-left p-3 rounded-lg transition-all ${c.id === currentContactId ? 'bg-blue-50 border border-blue-200' : 'hover:bg-gray-50 active:bg-gray-100'}`}
            >
              <p className="text-sm font-semibold text-gray-900">{c.name}</p>
              <div className="flex items-center gap-2 mt-1">
                <Mail className="w-3 h-3 text-gray-400 flex-shrink-0" />
                <p className="text-xs text-gray-500 font-mono truncate">{c.email}</p>
              </div>
              {c.position && (
                <p className="text-xs text-gray-400 mt-1">{c.position}</p>
              )}
            </button>
          ))}
        </div>
      </div>
    );
  };

  // Contact Card Component
  const ContactCard = ({ contact }) => {
    const statusInfo = getStatusInfo(contact.status);
    const StatusIcon = statusInfo.icon;
    const overdue = isOverdue(contact.next_action_date);
    const today = isToday(contact.next_action_date);
    const isEmailNotWorking = contact.status === 'email_not_working';
    const companyContacts = getCompanyContacts(contact.company_name);
    const hasMultipleContacts = companyContacts.length > 1;
    const isHighlighted = highlightedContact === contact.id;

    return (
      <div 
        ref={el => contactRefs.current[contact.id] = el}
        className={`bg-white rounded-xl border transition-all p-4 ${
          isEmailNotWorking 
            ? 'border-red-300 bg-red-50/50' 
            : isHighlighted
              ? 'border-blue-500 ring-2 ring-blue-300 shadow-lg'
              : overdue 
                ? 'border-red-300 shadow-red-100' 
                : today 
                  ? 'border-amber-300 shadow-amber-100' 
                  : 'border-gray-200'
        } shadow-sm hover:shadow-md`}
        data-testid={`contact-card-${contact.id}`}
      >
        {/* Email Not Working Warning Banner */}
        {isEmailNotWorking && (
          <div className="flex items-center gap-2 bg-red-100 text-red-700 rounded-lg px-3 py-2 mb-3 -mt-1 -mx-1">
            <XCircle className="w-4 h-4 flex-shrink-0" />
            <span className="text-sm font-medium">Do Not Contact - Email Not Functioning</span>
          </div>
        )}

        {/* Header - Name & Company */}
        <div className="flex items-start justify-between gap-2 mb-3">
          <div className="min-w-0 flex-1">
            {/* Copyable Name - Large touch target on mobile */}
            <button 
              onClick={() => copyText(contact.name)}
              className="flex items-center gap-2 group text-left w-full"
            >
              <h3 className="font-semibold text-gray-900 truncate group-active:text-blue-600 sm:group-hover:text-blue-600">
                {contact.name}
              </h3>
              <span className="flex-shrink-0 p-1.5 rounded-md bg-gray-100 sm:bg-transparent sm:group-hover:bg-gray-100 transition-all">
                {copiedText === contact.name ? <Check className="w-4 h-4 text-green-600" /> : <Copy className="w-4 h-4 text-gray-400" />}
              </span>
            </button>
            
            {/* Copyable Company with Click for Multiple Contacts (mobile) / Hover (desktop) */}
            <div className="relative mt-1">
              <div className="flex items-center gap-1">
                <Building2 className="w-3 h-3 flex-shrink-0 text-gray-400" />
                {hasMultipleContacts ? (
                  <button
                    onClick={() => setHoveredCompany(hoveredCompany === contact.id ? null : contact.id)}
                    className="text-sm text-blue-600 font-medium flex items-center gap-1 active:text-blue-800"
                  >
                    <span className="underline decoration-dotted underline-offset-2">{contact.company_name}</span>
                    <span className="bg-blue-100 text-blue-700 px-1.5 py-0.5 rounded-full text-xs font-bold">{companyContacts.length}</span>
                  </button>
                ) : (
                  <button
                    onClick={() => copyText(contact.company_name)}
                    className="text-sm text-gray-500 truncate active:text-blue-600 flex items-center gap-1"
                  >
                    {contact.company_name}
                    <span className="p-1 rounded opacity-60">
                      {copiedText === contact.company_name ? <Check className="w-3 h-3 text-green-600" /> : <Copy className="w-3 h-3 text-gray-400" />}
                    </span>
                  </button>
                )}
              </div>
              
              {/* Company Contacts Tooltip */}
              {hoveredCompany === contact.id && hasMultipleContacts && (
                <CompanyTooltip 
                  companyName={contact.company_name} 
                  currentContactId={contact.id} 
                  onClose={() => setHoveredCompany(null)}
                />
              )}
            </div>
          </div>
          <span className={`px-2.5 py-1 rounded-full text-xs font-semibold whitespace-nowrap ${statusInfo.color}`}>
            {contact.status === 'other' ? (contact.status_other_description || 'Other') : 
             contact.status === 'email_not_working' ? 'Bad Email' : statusInfo.label.split(' ')[0]}
          </span>
        </div>

        {/* Info Row */}
        <div className="flex flex-wrap gap-x-4 gap-y-1 text-sm text-gray-600 mb-3">
          {contact.position && (
            <span className="flex items-center gap-1 truncate">
              <Briefcase className="w-3 h-3 flex-shrink-0" />
              <span className="truncate">{contact.position}</span>
            </span>
          )}
          {contact.location && (
            <span className="flex items-center gap-1 truncate">
              <MapPin className="w-3 h-3 flex-shrink-0" />
              <span className="truncate">{contact.location}</span>
            </span>
          )}
        </div>

        {/* Email - Easy to copy with large touch target */}
        <button
          onClick={() => copyText(contact.email)}
          className={`w-full flex items-center gap-3 rounded-xl px-4 py-3 mb-3 transition-all active:scale-[0.98] ${
            isEmailNotWorking 
              ? 'bg-red-100 border border-red-200' 
              : copiedText === contact.email 
                ? 'bg-green-50 border border-green-200' 
                : 'bg-gray-50 hover:bg-gray-100 active:bg-gray-100'
          }`}
          data-testid={`copy-email-${contact.id}`}
        >
          <Mail className={`w-5 h-5 flex-shrink-0 ${isEmailNotWorking ? 'text-red-400' : 'text-gray-400'}`} />
          <span className={`text-sm truncate flex-1 font-mono text-left ${isEmailNotWorking ? 'text-red-600 line-through' : 'text-gray-700'}`}>
            {contact.email}
          </span>
          <span className={`flex items-center gap-1 text-xs font-medium px-2 py-1 rounded-md ${
            copiedText === contact.email ? 'bg-green-100 text-green-700' : 'bg-white text-gray-500'
          }`}>
            {copiedText === contact.email ? (
              <><Check className="w-3.5 h-3.5" /> Copied!</>
            ) : (
              <><Copy className="w-3.5 h-3.5" /> Copy</>
            )}
          </span>
        </button>

        {/* Next Action - Only show if not email_not_working */}
        {!isEmailNotWorking && contact.next_action_date && (
          <div className={`rounded-xl px-4 py-3 mb-3 ${overdue ? 'bg-red-50 border border-red-200' : today ? 'bg-amber-50 border border-amber-200' : 'bg-blue-50 border border-blue-100'}`}>
            <div className="flex items-center gap-2">
              <Clock className={`w-5 h-5 ${overdue ? 'text-red-500' : today ? 'text-amber-500' : 'text-blue-500'}`} />
              <span className={`text-sm font-semibold ${overdue ? 'text-red-700' : today ? 'text-amber-700' : 'text-blue-700'}`}>
                {overdue ? '⚠️ Overdue' : today ? '📅 Due Today' : 'Next'}: {formatDate(contact.next_action_date)}
              </span>
            </div>
            {contact.next_action_description && (
              <p className="text-sm text-gray-600 mt-2 ml-7">{contact.next_action_description}</p>
            )}
          </div>
        )}

        {/* Notes Preview */}
        {contact.notes && (
          <p className="text-sm text-gray-500 mb-3 line-clamp-2 bg-gray-50 rounded-lg px-3 py-2">{contact.notes}</p>
        )}

        {/* Actions - Mobile friendly with larger touch targets */}
        <div className="flex items-center gap-2 pt-3 border-t border-gray-100">
          {!isEmailNotWorking && (
            <Button
              size="sm"
              onClick={() => setShowMarkComplete(contact.id)}
              className="bg-green-600 hover:bg-green-700 active:bg-green-800 h-10 px-4 text-sm font-medium"
              data-testid={`mark-complete-${contact.id}`}
            >
              <CheckCircle2 className="w-4 h-4 mr-1.5" />
              Mark Done
            </Button>
          )}
          <div className="flex items-center gap-1 ml-auto">
            <button
              onClick={() => setSelectedContact(contact)}
              className="p-2.5 text-gray-400 hover:text-blue-600 hover:bg-blue-50 active:bg-blue-100 rounded-lg transition-colors"
              title="View details & history"
            >
              <History className="w-5 h-5" />
            </button>
            <button
              onClick={() => openEdit(contact)}
              className="p-2.5 text-gray-400 hover:text-amber-600 hover:bg-amber-50 active:bg-amber-100 rounded-lg transition-colors"
              title="Edit contact"
            >
              <Edit2 className="w-5 h-5" />
            </button>
            <button
              onClick={() => handleDelete(contact.id)}
              className="p-2.5 text-gray-400 hover:text-red-600 hover:bg-red-50 active:bg-red-100 rounded-lg transition-colors"
              title="Delete contact"
            >
              <Trash2 className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Mark Complete Modal (inline) - Rendered separately to avoid input focus loss */}
        {showMarkComplete === contact.id && (
          <MarkCompleteInline 
            contactId={contact.id}
            markCompleteForm={markCompleteForm}
            setMarkCompleteForm={setMarkCompleteForm}
            handleMarkComplete={handleMarkComplete}
            setShowMarkComplete={setShowMarkComplete}
          />
        )}
      </div>
    );
  };

  // Contact Group Component
  const ContactGroup = ({ title, contacts, icon: Icon, color, badge }) => {
    const isExpanded = expandedGroups[badge];
    
    if (contacts.length === 0) return null;

    return (
      <div className="mb-6">
        <button
          onClick={() => toggleGroup(badge)}
          className={`w-full flex items-center justify-between p-3 rounded-lg ${color} mb-3`}
        >
          <div className="flex items-center gap-2">
            <Icon className="w-5 h-5" />
            <span className="font-semibold">{title}</span>
            <span className="px-2 py-0.5 bg-white/50 rounded-full text-sm">{contacts.length}</span>
          </div>
          {isExpanded ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
        </button>
        
        {isExpanded && (
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
            {contacts.map(contact => (
              <ContactCard key={contact.id} contact={contact} />
            ))}
          </div>
        )}
      </div>
    );
  };

  if (loading) {
    return (
      <div className="p-4 md:p-6 lg:p-8">
        <div className="animate-pulse space-y-4">
          <div className="h-8 bg-gray-200 rounded w-48"></div>
          <div className="h-24 bg-gray-200 rounded"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-4 md:p-6 lg:p-8 max-w-7xl mx-auto">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900">Outreach Database</h1>
          <p className="text-gray-500 text-sm mt-1">{contacts.length} contact{contacts.length !== 1 ? 's' : ''}</p>
        </div>
        <Button
          onClick={() => { setShowForm(true); setEditingContact(null); setFormData(emptyForm); }}
          className="bg-[#1a1f4e] hover:bg-[#2a2f5e]"
          data-testid="add-contact-btn"
        >
          <Plus className="w-4 h-4 mr-2" />
          Add Contact
        </Button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
        <div className="bg-red-50 border border-red-200 rounded-xl p-4">
          <div className="flex items-center gap-2 text-red-600 mb-1">
            <AlertCircle className="w-5 h-5" />
            <span className="text-2xl font-bold">{stats.overdue}</span>
          </div>
          <p className="text-sm text-red-600">Overdue</p>
        </div>
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-4">
          <div className="flex items-center gap-2 text-amber-600 mb-1">
            <Clock className="w-5 h-5" />
            <span className="text-2xl font-bold">{stats.today}</span>
          </div>
          <p className="text-sm text-amber-600">Due Today</p>
        </div>
        <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
          <div className="flex items-center gap-2 text-blue-600 mb-1">
            <Calendar className="w-5 h-5" />
            <span className="text-2xl font-bold">{stats.upcoming}</span>
          </div>
          <p className="text-sm text-blue-600">Upcoming</p>
        </div>
        <div className="bg-gray-50 border border-gray-200 rounded-xl p-4">
          <div className="flex items-center gap-2 text-gray-600 mb-1">
            <User className="w-5 h-5" />
            <span className="text-2xl font-bold">{stats.no_date}</span>
          </div>
          <p className="text-sm text-gray-600">No Date Set</p>
        </div>
      </div>

      {/* Search */}
      <div className="relative mb-6">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
        <Input
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          placeholder="Search by name, email, company, or location..."
          className="pl-10"
          data-testid="search-input"
        />
      </div>

      {/* Contact Groups */}
      <ContactGroup 
        title="Overdue Actions" 
        contacts={filteredGrouped.overdue} 
        icon={AlertCircle} 
        color="bg-red-100 text-red-700"
        badge="overdue"
      />
      <ContactGroup 
        title="Due Today" 
        contacts={filteredGrouped.today} 
        icon={Clock} 
        color="bg-amber-100 text-amber-700"
        badge="today"
      />
      <ContactGroup 
        title="Upcoming Actions" 
        contacts={filteredGrouped.upcoming} 
        icon={Calendar} 
        color="bg-blue-100 text-blue-700"
        badge="upcoming"
      />
      <ContactGroup 
        title="No Action Date Set" 
        contacts={filteredGrouped.no_date} 
        icon={User} 
        color="bg-gray-100 text-gray-700"
        badge="no_date"
      />

      {contacts.length === 0 && (
        <div className="bg-white rounded-xl border border-gray-200 p-8 text-center">
          <User className="w-12 h-12 text-gray-300 mx-auto mb-3" />
          <p className="text-gray-500">No contacts yet. Add your first outreach contact to get started.</p>
        </div>
      )}

      {/* Add/Edit Contact Modal */}
      {showForm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between">
              <h2 className="text-lg font-semibold">{editingContact ? 'Edit Contact' : 'Add New Contact'}</h2>
              <button onClick={() => { setShowForm(false); setEditingContact(null); }} className="text-gray-400 hover:text-gray-600">
                <X className="w-5 h-5" />
              </button>
            </div>
            
            <form onSubmit={handleSubmit} className="p-6 space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="sm:col-span-2">
                  <Label className="text-gray-700 mb-1.5 block">Email *</Label>
                  <Input
                    type="email"
                    required
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    placeholder="email@company.com"
                    data-testid="contact-email-input"
                  />
                </div>
                <div>
                  <Label className="text-gray-700 mb-1.5 block">Name *</Label>
                  <Input
                    required
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    placeholder="John Smith"
                    data-testid="contact-name-input"
                  />
                </div>
                <div className="relative">
                  <Label className="text-gray-700 mb-1.5 block">Company *</Label>
                  <Input
                    required
                    value={formData.company_name}
                    onChange={(e) => {
                      setFormData({ ...formData, company_name: e.target.value });
                      setShowCompanyDropdown(true);
                    }}
                    onFocus={() => setShowCompanyDropdown(true)}
                    onBlur={() => setTimeout(() => setShowCompanyDropdown(false), 250)}
                    placeholder="Company Ltd"
                    data-testid="contact-company-input"
                    autoComplete="off"
                  />
                  {/* Company Autocomplete Dropdown - Mobile optimized */}
                  {showCompanyDropdown && companySuggestions.length > 0 && (
                    <div className="absolute z-50 left-0 right-0 top-full mt-1 bg-white border border-gray-200 rounded-xl shadow-xl overflow-hidden">
                      <div className="bg-gradient-to-r from-blue-500 to-blue-600 px-4 py-2">
                        <p className="text-xs font-semibold text-white">Select existing company</p>
                      </div>
                      <div className="max-h-52 overflow-y-auto">
                        {companySuggestions.map((company, idx) => (
                          <button
                            key={idx}
                            type="button"
                            onClick={() => {
                              setFormData({ ...formData, company_name: company });
                              setShowCompanyDropdown(false);
                            }}
                            className="w-full text-left px-4 py-3 hover:bg-blue-50 active:bg-blue-100 flex items-center gap-3 transition-colors border-b border-gray-50 last:border-0"
                          >
                            <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center flex-shrink-0">
                              <Building2 className="w-5 h-5 text-blue-600" />
                            </div>
                            <div className="min-w-0 flex-1">
                              <p className="font-medium text-gray-900 truncate">{company}</p>
                              <p className="text-xs text-gray-500">
                                {getCompanyContacts(company).length} contact{getCompanyContacts(company).length !== 1 ? 's' : ''}
                              </p>
                            </div>
                            <Check className="w-5 h-5 text-blue-500 flex-shrink-0" />
                          </button>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
                <div>
                  <Label className="text-gray-700 mb-1.5 block">Position</Label>
                  <Input
                    value={formData.position}
                    onChange={(e) => setFormData({ ...formData, position: e.target.value })}
                    placeholder="Property Manager"
                  />
                </div>
                <div>
                  <Label className="text-gray-700 mb-1.5 block">Location</Label>
                  <Input
                    value={formData.location}
                    onChange={(e) => setFormData({ ...formData, location: e.target.value })}
                    placeholder="London"
                  />
                </div>
              </div>

              <div>
                <Label className="text-gray-700 mb-1.5 block">Status</Label>
                <select
                  value={formData.status}
                  onChange={(e) => setFormData({ 
                    ...formData, 
                    status: e.target.value,
                    last_action_date: e.target.value === 'scheduled' ? '' : formData.last_action_date,
                    next_action_date: e.target.value === 'email_not_working' ? '' : formData.next_action_date,
                    next_action_description: e.target.value === 'email_not_working' ? '' : formData.next_action_description
                  })}
                  className="w-full h-11 px-4 rounded-lg border border-gray-200 focus:border-[#1a1f4e] outline-none"
                  data-testid="contact-status-select"
                >
                  {STATUS_OPTIONS.map(opt => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </select>
              </div>

              {formData.status === 'email_not_working' && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                  <p className="text-sm text-red-700 flex items-center gap-2">
                    <XCircle className="w-4 h-4" />
                    This contact will be marked as "Do Not Contact" - no follow-up actions needed.
                  </p>
                </div>
              )}

              {formData.status === 'other' && (
                <div>
                  <Label className="text-gray-700 mb-1.5 block">Status Description *</Label>
                  <Input
                    required
                    value={formData.status_other_description}
                    onChange={(e) => setFormData({ ...formData, status_other_description: e.target.value })}
                    placeholder="e.g., Meeting scheduled, Waiting for response..."
                  />
                </div>
              )}

              {formData.status !== 'email_not_working' && (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  {formData.status !== 'scheduled' && (
                    <div>
                      <Label className="text-gray-700 mb-1.5 block">Last Action Date</Label>
                      <Input
                        type="date"
                        value={formData.last_action_date}
                        onChange={(e) => setFormData({ ...formData, last_action_date: e.target.value })}
                      />
                    </div>
                  )}
                  <div className={formData.status === 'scheduled' ? 'sm:col-span-2' : ''}>
                    <Label className="text-gray-700 mb-1.5 block">Next Action Date</Label>
                    <Input
                      type="date"
                      value={formData.next_action_date}
                      onChange={(e) => setFormData({ ...formData, next_action_date: e.target.value })}
                      data-testid="contact-next-date-input"
                    />
                  </div>
                </div>
              )}

              {formData.status !== 'email_not_working' && (
                <div>
                  <Label className="text-gray-700 mb-1.5 block">Next Action Description</Label>
                  <Input
                    value={formData.next_action_description}
                    onChange={(e) => setFormData({ ...formData, next_action_description: e.target.value })}
                    placeholder="What needs to be done next?"
                  />
                </div>
              )}

              <div>
                <Label className="text-gray-700 mb-1.5 block">Notes</Label>
                <textarea
                  rows={3}
                  value={formData.notes}
                  onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                  className="w-full px-4 py-3 rounded-lg border border-gray-200 focus:border-[#1a1f4e] outline-none resize-none"
                  placeholder="Where you're at with this contact..."
                  data-testid="contact-notes-input"
                />
              </div>

              <div className="flex gap-3 pt-2">
                <Button type="submit" disabled={saving} className="bg-[#1a1f4e] hover:bg-[#2a2f5e] flex-1" data-testid="save-contact-btn">
                  {saving ? 'Saving...' : editingContact ? 'Update Contact' : 'Add Contact'}
                </Button>
                <Button type="button" variant="outline" onClick={() => { setShowForm(false); setEditingContact(null); }}>
                  Cancel
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Contact Detail & History Modal */}
      {selectedContact && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between">
              <h2 className="text-lg font-semibold">Contact Details & History</h2>
              <button onClick={() => setSelectedContact(null)} className="text-gray-400 hover:text-gray-600">
                <X className="w-5 h-5" />
              </button>
            </div>
            
            <div className="p-6">
              {/* Contact Info */}
              <div className={`rounded-xl p-4 mb-6 ${selectedContact.status === 'email_not_working' ? 'bg-red-50 border border-red-200' : 'bg-gray-50'}`}>
                {selectedContact.status === 'email_not_working' && (
                  <div className="flex items-center gap-2 bg-red-100 text-red-700 rounded-lg px-3 py-2 mb-4">
                    <XCircle className="w-5 h-5" />
                    <span className="font-medium">Do Not Contact - Email Not Functioning</span>
                  </div>
                )}
                
                <div className="flex items-start justify-between mb-4">
                  <div>
                    {/* Copyable Name */}
                    <div className="flex items-center gap-2 group">
                      <h3 
                        className="text-xl font-semibold text-gray-900 cursor-pointer hover:text-blue-600"
                        onClick={() => copyText(selectedContact.name)}
                      >
                        {selectedContact.name}
                      </h3>
                      <button onClick={() => copyText(selectedContact.name)} className="opacity-0 group-hover:opacity-100 p-1 hover:bg-gray-200 rounded">
                        {copiedText === selectedContact.name ? <Check className="w-4 h-4 text-green-600" /> : <Copy className="w-4 h-4 text-gray-400" />}
                      </button>
                    </div>
                    {/* Copyable Position & Company */}
                    <div className="flex items-center gap-1 group">
                      <p className="text-gray-600">{selectedContact.position} at </p>
                      <span 
                        className="text-gray-600 cursor-pointer hover:text-blue-600"
                        onClick={() => copyText(selectedContact.company_name)}
                      >
                        {selectedContact.company_name}
                      </span>
                      <button onClick={() => copyText(selectedContact.company_name)} className="opacity-0 group-hover:opacity-100 p-1 hover:bg-gray-200 rounded">
                        {copiedText === selectedContact.company_name ? <Check className="w-4 h-4 text-green-600" /> : <Copy className="w-4 h-4 text-gray-400" />}
                      </button>
                    </div>
                  </div>
                  <span className={`px-3 py-1 rounded-full text-sm font-medium ${getStatusInfo(selectedContact.status).color}`}>
                    {selectedContact.status === 'other' ? selectedContact.status_other_description : getStatusInfo(selectedContact.status).label}
                  </span>
                </div>
                
                {/* Copyable Email */}
                <div className={`flex items-center gap-2 rounded-lg px-3 py-2 mb-3 ${selectedContact.status === 'email_not_working' ? 'bg-red-100 line-through' : 'bg-white'}`}>
                  <Mail className={`w-4 h-4 ${selectedContact.status === 'email_not_working' ? 'text-red-400' : 'text-gray-400'}`} />
                  <span className={`font-mono flex-1 ${selectedContact.status === 'email_not_working' ? 'text-red-600' : 'text-gray-700'}`}>{selectedContact.email}</span>
                  <button onClick={() => copyText(selectedContact.email)} className="p-1 hover:bg-gray-100 rounded">
                    {copiedText === selectedContact.email ? <Check className="w-4 h-4 text-green-600" /> : <Copy className="w-4 h-4 text-gray-500" />}
                  </button>
                </div>
                
                {selectedContact.location && (
                  <p className="text-sm text-gray-600 flex items-center gap-1 mb-3">
                    <MapPin className="w-4 h-4" /> {selectedContact.location}
                  </p>
                )}

                {/* Next Action */}
                {selectedContact.status !== 'email_not_working' && selectedContact.next_action_date && (
                  <div className={`rounded-lg px-3 py-2 mb-3 ${isOverdue(selectedContact.next_action_date) ? 'bg-red-100' : isToday(selectedContact.next_action_date) ? 'bg-amber-100' : 'bg-blue-100'}`}>
                    <p className={`text-sm font-medium ${isOverdue(selectedContact.next_action_date) ? 'text-red-700' : isToday(selectedContact.next_action_date) ? 'text-amber-700' : 'text-blue-700'}`}>
                      Next Action: {formatDate(selectedContact.next_action_date)}
                    </p>
                    {selectedContact.next_action_description && (
                      <p className="text-sm text-gray-600 mt-1">{selectedContact.next_action_description}</p>
                    )}
                  </div>
                )}
                
                {selectedContact.notes && (
                  <div className="pt-3 border-t border-gray-200">
                    <p className="text-sm font-medium text-gray-700 mb-1">Notes:</p>
                    <p className="text-gray-600">{selectedContact.notes}</p>
                  </div>
                )}
              </div>

              {/* Timeline / History */}
              <div>
                <h4 className="font-semibold text-gray-900 mb-4 flex items-center gap-2">
                  <History className="w-5 h-5" />
                  Timeline
                </h4>
                
                {(selectedContact.history || []).length > 0 ? (
                  <div className="space-y-3 mb-4">
                    {[...(selectedContact.history || [])]
                      .sort((a, b) => new Date(b.date) - new Date(a.date))
                      .map((entry, idx) => (
                      <div key={entry.id || idx} className="flex gap-3">
                        <div className="w-2 h-2 mt-2 rounded-full bg-[#1a1f4e] flex-shrink-0"></div>
                        <div className="flex-1 bg-gray-50 rounded-lg p-3">
                          <div className="flex items-center justify-between">
                            <span className="font-medium text-gray-900">{entry.action}</span>
                            <span className="text-sm text-gray-500">{formatDate(entry.date)}</span>
                          </div>
                          {entry.notes && <p className="text-sm text-gray-600 mt-1">{entry.notes}</p>}
                          {!entry.auto_generated && (
                            <span className="text-xs text-gray-400 mt-1 inline-block">Manually added</span>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-gray-500 text-sm mb-4">No history entries yet.</p>
                )}

                {/* Add History Entry */}
                <div className="bg-blue-50 rounded-xl p-4">
                  <p className="text-sm font-medium text-blue-700 mb-3">Add Manual History Entry</p>
                  <div className="space-y-3">
                    <div className="grid grid-cols-2 gap-3">
                      <Input
                        type="date"
                        value={historyForm.date}
                        onChange={(e) => setHistoryForm({ ...historyForm, date: e.target.value })}
                        placeholder="Date"
                      />
                      <Input
                        value={historyForm.action}
                        onChange={(e) => setHistoryForm({ ...historyForm, action: e.target.value })}
                        placeholder="Action taken"
                      />
                    </div>
                    <Input
                      value={historyForm.notes}
                      onChange={(e) => setHistoryForm({ ...historyForm, notes: e.target.value })}
                      placeholder="Notes (optional)"
                    />
                    <Button 
                      size="sm"
                      onClick={() => handleAddHistory(selectedContact.id)}
                      disabled={!historyForm.date || !historyForm.action}
                      className="bg-blue-600 hover:bg-blue-700"
                    >
                      Add Entry
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default OutreachDatabase;
